/**
 * Editor Compiler — public facade.
 *
 * Deterministically converts a DesignSpec into the native iVisora editor
 * document format (the `template_json` shape the editor already loads):
 *
 *   { canvas_width, canvas_height, pages: [{ id, name, elements[], background }] }
 *
 * Ordering rules mirror the editor's own conventions: stacking is element
 * array order (spec zIndex decides it), groups are inserted just above
 * their topmost member, and members carry `parentId`. The compiled
 * document is validated for editor compatibility AND spec fidelity before
 * it is returned — an invalid document never leaves this module.
 *
 * Nothing renders here: no Fabric.js, no canvas, no editor imports.
 */

import { mapObjectToElement, mapGroupToElement } from './elementMappers';
import { validateEditorDocument } from '../../templateEngine/documentValidator';

export const compileEditorDocument = (spec) => {
  // Background object becomes the page background (the editor's native slot).
  const background = spec.objects.find((obj) => obj.sectionId === 'canvas');
  const contentObjects = spec.objects
    .filter((obj) => obj.sectionId !== 'canvas')
    .sort((a, b) => a.zIndex - b.zIndex); // spec z-order → editor array order

  const elements = contentObjects.map(mapObjectToElement);
  const indexById = new Map(elements.map((el, i) => [el.id, i]));

  // Groups: assign member parentIds, then insert each group element just
  // above its topmost member (the editor's grouping convention).
  spec.groups.forEach((group) => {
    const memberIndexes = group.memberIds
      .map((id) => indexById.get(id))
      .filter((i) => i !== undefined);
    if (memberIndexes.length === 0) return;

    group.memberIds.forEach((id) => {
      const idx = indexById.get(id);
      if (idx !== undefined) elements[idx] = { ...elements[idx], parentId: group.id };
    });

    const insertAt = Math.max(...memberIndexes) + 1;
    elements.splice(insertAt, 0, mapGroupToElement(group));
    elements.forEach((el, i) => indexById.set(el.id, i)); // reindex after splice
  });

  const doc = {
    canvas_width: spec.canvas.width,
    canvas_height: spec.canvas.height,
    metadata: {
      ...spec.metadata,
      specId: spec.id,
      specVersion: spec.version,
      compiler: 'aiTemplateGenerator/editorCompiler',
    },
    pages: [
      {
        id: `${spec.id}-page-1`,
        name: 'Page 1',
        background: background?.shape.fill || spec.canvas.background,
        elements,
      },
    ],
  };

  const { valid, errors } = validateEditorDocument(doc, spec);
  if (!valid) throw new Error(`Editor compilation failed: ${errors.join('; ')}`);
  return doc;
};

export { validateEditorDocument } from '../../templateEngine/documentValidator';
